import { jwtDecode } from 'jwt-decode';
import { jwtVerify, createRemoteJWKSet } from 'jose';
import type { User } from '@/types/Auth';
import { logger } from '@equip/utils/logger';

// Security Test Coverage Gap (Code Review Issue #CR3)
// CRITICAL: Zero test coverage for security-critical authentication code.
// Missing tests for:
// - JWT signature verification (must reject forged/tampered tokens)
// - PKCE code_challenge generation (RFC 7636 compliance)
// - Authorization code replay detection
// - Token refresh retry with jitter behavior
// - Silent SSO error handling edge cases
// - Idle timeout countdown accuracy
// - Cross-site token validation
// Recommendation: Add integration tests with real crypto operations before production deploy.

interface JwtClaims {
  sub: string;
  /** Azure Entra/Workforce object ID — a stable UUID that identifies the user.  Use
   *  this as the authoritative user ID when present; `sub` in Azure Workforce is a
   *  pairwise opaque string (not a UUID) and cannot be cast to uuid by Postgres. */
  oid?: string;
  preferred_username: string;
  scp?: string;
  email?: string;
  given_name?: string;
  family_name?: string;
  // Standard Azure Workforce / Entra ID app roles claim (top-level array).
  // This is the primary claim for PKCE user tokens when app roles are assigned
  // to the user in the Workforce app registration.
  roles?: string[];
  'https://equip.gov/roles'?: string[];
  'https://equip'?: {
    'gov/roles'?: string[];
  };
  realm_access?: {
    roles?: string[];
  };
  org_id?: string;
  aud: string | string[];
  iss: string;
  exp: number;
  iat: number;
}

// JWKS configuration - lazy initialization to handle URL changes
let jwksCache: ReturnType<typeof createRemoteJWKSet> | null = null;
let cachedJwksUrl: string | null = null;

// Expected values for validation
// VITE_OIDC_AUTHORITY is the Azure Workforce tenant authority base URL.
// The JWKS endpoint is derived from it as: {authority}/discovery/v2.0/keys
// (Azure Workforce places the JWKS under the /discovery/ path, not directly at /v2.0/keys)
const getOidcJwksUri = () => {
  const authority = import.meta.env.VITE_OIDC_AUTHORITY ?? '';
  if (authority) return `${authority}/discovery/v2.0/keys`;
  // Fallback: derive from current origin (should not be reached in production).
  return `${window.location.origin}/discovery/v2.0/keys`;
};
const getOidcIssuer = () => {
  const authority = import.meta.env.VITE_OIDC_AUTHORITY ?? '';
  return authority ? `${authority}/v2.0` : `${window.location.origin}/v2.0`;
};
// Azure Workforce sets `aud` to the registered client UUID, not a display name.
// Audience validation is handled server-side; the client only checks issuer + signature.

/**
 * Gets JWKS endpoint with caching and retry logic.
 * Reinitializes if OIDC authority URL changes (for testing or multi-tenant scenarios).
 */
const getJWKS = () => {
  const JWKS_URL = getOidcJwksUri();

  // Reinitialize if URL changed
  if (!jwksCache || cachedJwksUrl !== JWKS_URL) {
    cachedJwksUrl = JWKS_URL;
    jwksCache = createRemoteJWKSet(new URL(JWKS_URL), {
      cacheMaxAge: 60000, // 60 seconds - faster response to key rotation
      cooldownDuration: 500, // 500ms between refetch attempts (reduced from 30s to avoid blocking logins during cold JWKS fetch)
    });
  }

  return jwksCache;
};

/**
 * Validates JWT token for security compliance with signature verification.
 * Checks signature, expiration, algorithm, and issuer claims.
 * Audience is intentionally not checked client-side: Azure Workforce sets aud to the
 * registered client UUID which varies by environment; the backend (actions-api)
 * performs the authoritative audience validation via JWKS on every request.
 * @throws Error if token is invalid
 */
export const validateToken = async (token: string): Promise<void> => {
  try {
    const OIDC_ISSUER = getOidcIssuer();
    const JWKS = getJWKS();

    // Verify signature and validate claims using jose library with retry/caching
    const { payload } = await jwtVerify(token, JWKS, {
      issuer: OIDC_ISSUER,
      algorithms: ['RS256'], // Only allow RS256 (asymmetric signing)
      clockTolerance: 30, // 30-second grace period for clock skew
    });

    // Additional validation for issued-at time
    const now = Date.now() / 1000;
    if (payload.iat && payload.iat > now + 30) {
      throw new Error('Token issued time is in the future');
    }

    logger.debug('Token validation successful', {
      component: 'jwtUtils',
      issuer: payload.iss,
      audience: payload.aud,
    });
  } catch (error) {
    if (error instanceof Error) {
      logger.error('Token validation failed', {
        component: 'jwtUtils',
        error: error.message,
        errorType: error.name,
      });
      throw error;
    }
    throw new Error('Token validation failed: unable to verify token');
  }
};

export const parseJwtClaims = (token: string): User => {
  try {
    const claims = jwtDecode<JwtClaims>(token);

    // Extract roles. Azure Workforce places roles in:
    //   1. `roles` (top-level array) — standard Workforce/Entra app roles claim for PKCE user tokens
    //   2. `https://equip.gov/roles` — custom claim (flat or nested) for legacy compatibility
    //   3. `realm_access.roles` — the token shape minted by local dev/test tooling
    //      (Tiltfile, reset-db.sh, smoke tests, benchmark harness)
    // NOTE: use explicit Array.isArray + length checks because `[] || fallback`
    // returns `[]` (arrays are truthy), silently dropping all roles.
    const workforceRoles = claims.roles;
    const realmRoles = claims.realm_access?.roles;
    const customRolesFlat = claims['https://equip.gov/roles'];
    const customRolesNested = claims['https://equip']?.['gov/roles'];

    const rawRoles: string[] =
      (Array.isArray(workforceRoles) && workforceRoles.length > 0 ? workforceRoles : null) ??
      (Array.isArray(realmRoles) && realmRoles.length > 0 ? realmRoles : null) ??
      (Array.isArray(customRolesFlat) && customRolesFlat.length > 0 ? customRolesFlat : null) ??
      (Array.isArray(customRolesNested) && customRolesNested.length > 0
        ? customRolesNested
        : null) ??
      [];

    // Normalize Workforce app role names: strip the 'equip_' prefix so
    // 'equip_user' → 'user', 'equip_admin' → 'admin', etc.
    // This lets the Workforce app registration keep its 'equip_*' naming convention
    // while the application code uses the short names consistently.
    const roles = rawRoles.map((r) => (r.startsWith('equip_') ? r.slice(6) : r));

    logger.debug('Resolved JWT roles', {
      component: 'jwtUtils',
      hasSub: Boolean(claims.sub),
      username: claims.preferred_username,
      audience: claims.aud,
      scope: claims.scp ?? null,
      source:
        Array.isArray(workforceRoles) && workforceRoles.length > 0
          ? 'Workforce roles'
          : Array.isArray(realmRoles) && realmRoles.length > 0
            ? 'realm_access'
            : Array.isArray(customRolesFlat) && customRolesFlat.length > 0
              ? 'Workforce flat'
              : Array.isArray(customRolesNested) && customRolesNested.length > 0
                ? 'Workforce nested'
                : 'none',
      rawRoles,
      roles,
    });

    return {
      id: claims.oid ?? claims.sub,
      username: claims.preferred_username,
      email: claims.email ?? '',
      firstName: claims.given_name,
      lastName: claims.family_name,
      roles: roles,
      orgId: claims.org_id,
    };
  } catch (error) {
    logger.error('Failed to parse JWT claims', { component: 'jwtUtils', error: String(error) });
    throw new Error('Invalid JWT token');
  }
};

export const getTokenExpiration = (token: string): Date | null => {
  try {
    const claims = jwtDecode<JwtClaims>(token);
    return new Date(claims.exp * 1000);
  } catch {
    return null;
  }
};

/**
 * Extract the actor UUID from an access token.
 * Prefers the `oid` claim (Azure OID — always a UUID) over `sub`
 * (which in Azure Workforce is a pairwise opaque string, not a UUID).
 * Returns null if neither claim is a valid UUID.
 */
export const getActorIdFromToken = (token: string): string | null => {
  try {
    const claims = jwtDecode<JwtClaims & { oid?: string }>(token);
    const oid = typeof claims.oid === 'string' ? claims.oid.trim() : '';
    if (oid && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(oid)) {
      return oid;
    }
    const sub = typeof claims.sub === 'string' ? claims.sub.trim() : '';
    if (sub && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(sub)) {
      return sub;
    }
    return null;
  } catch {
    return null;
  }
};
